import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { History, User, Package, Folder, Clock, Search } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const mockEditLogs = [
  { id: 1, timestamp: '2024-07-20 11:00:00', editor: 'Administrador Principal', editorEmail: 'admin@empresa.com', action: 'Producto Actualizado', itemType: 'Producto', itemName: 'Hoteles Patagonia 2025 - 2026', itemId: 1, details: 'Precio de $899.99 a $879.99' },
  { id: 2, timestamp: '2024-07-20 10:50:00', editor: 'Administrador Principal', editorEmail: 'admin@empresa.com', action: 'Usuario Creado', itemType: 'Usuario', itemName: 'Nuevo Usuario Agencia', itemId: 5, details: 'Rol: agency, Estado: active' },
  { id: 3, timestamp: '2024-07-19 16:30:00', editor: 'Administrador Principal', editorEmail: 'admin@empresa.com', action: 'Categoría Actualizada', itemType: 'Categoría', itemName: 'Hoteleria Chile', itemId: 1, details: 'Descripción actualizada' },
  { id: 4, timestamp: '2024-07-19 15:00:00', editor: 'Administrador Principal', editorEmail: 'admin@empresa.com', action: 'Producto Creado', itemType: 'Producto', itemName: 'Smartwatch Deportivo', itemId: 10, details: 'Categoría: Moda, Precio: $150.00' },
  { id: 5, timestamp: '2024-07-18 09:00:00', editor: 'Administrador Principal', editorEmail: 'admin@empresa.com', action: 'Usuario Actualizado', itemType: 'Usuario', itemName: 'Usuario de Prueba', itemId: 4, details: 'Estado de inactive a active' },
];

const EditHistoryPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [editLogs, setEditLogs] = useState(mockEditLogs);
  const [searchTerm, setSearchTerm] = useState('');

  if (!isAdmin) {
    navigate('/admin'); // Redirigir si no es administrador
    return null;
  }

  const filteredLogs = editLogs.filter(log =>
    log.editor.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.itemType.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.details.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getItemTypeIcon = (type) => {
    switch (type) {
      case 'Producto': return <Package className="w-4 h-4 text-blue-500" />;
      case 'Usuario': return <User className="w-4 h-4 text-green-500" />;
      case 'Categoría': return <Folder className="w-4 h-4 text-purple-500" />;
      default: return <History className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Historial de Ediciones</h2>
          <p className="text-xl text-gray-600">Rastrea todos los cambios realizados en el sistema.</p>
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative flex-1 max-w-md mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por editor, acción, tipo de ítem o detalles..."
              className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">Fecha y Hora</th>
                  <th className="py-3 px-6 text-left">Editor</th>
                  <th className="py-3 px-6 text-left">Acción</th>
                  <th className="py-3 px-6 text-left">Tipo de Ítem</th>
                  <th className="py-3 px-6 text-left">Ítem</th>
                  <th className="py-3 px-6 text-left">Detalles</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 text-sm font-light">
                {filteredLogs.map((log) => (
                  <motion.tr
                    key={log.id}
                    className="border-b border-gray-200 hover:bg-gray-50"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <td className="py-4 px-6 text-left whitespace-nowrap">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        {log.timestamp}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-left">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="font-medium">{log.editor}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-left">{log.action}</td>
                    <td className="py-4 px-6 text-left">
                      <div className="flex items-center gap-2">
                        {getItemTypeIcon(log.itemType)}
                        {log.itemType}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-left">{log.itemName} (ID: {log.itemId})</td>
                    <td className="py-4 px-6 text-left text-gray-600">{log.details}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredLogs.length === 0 && (
            <motion.div
              className="text-center py-12 text-gray-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              No se encontraron registros de edición que coincidan con tu búsqueda.
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default EditHistoryPage;